/**
 * Built-in stand-in drawings, used when /demo-drawings/ has no file for a type
 * or view. Deliberately simple and labelled "Platzhalter": they keep every
 * screen working, they are not meant to be mistaken for the real drawings.
 *
 * Orientation contract (same as the real PNGs): oben = nose up, vorne = seen
 * from the front, links = left side with the nose pointing left, innen = the
 * cabin from above, nose up.
 */
import { CABIN_PLACEHOLDER_SIZE, type BaseView } from '@fsv/core';

export type WingLayout = 'low' | 'high';

export interface Silhouette {
  svg: string;
  width: number;
  height: number;
}

const INK = '#10243A';

function wrap(width: number, height: number, body: string): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">` +
    `<g fill="none" stroke="${INK}" stroke-width="4" stroke-linejoin="round" stroke-linecap="round">${body}</g>` +
    `<text x="${width - 16}" y="${height - 16}" text-anchor="end" font-family="sans-serif" font-size="22" fill="${INK}" opacity="0.45">Platzhalter</text>` +
    `</svg>`
  );
}

function top(layout: WingLayout): Silhouette {
  const w = 1200;
  const h = 900;
  const fuselage =
    'M600 70 C635 70 655 115 660 175 L664 330 C664 430 640 610 616 812 L584 812 C560 610 536 430 536 330 L540 175 C545 115 565 70 600 70 Z';
  const wingL = 'M538 255 L70 288 Q44 290 44 312 L44 350 Q44 368 70 368 L538 372';
  const wingR = 'M662 255 L1130 288 Q1156 290 1156 312 L1156 350 Q1156 368 1130 368 L662 372';
  const stab = 'M588 735 L392 752 Q378 754 378 768 L378 792 Q378 804 392 804 L588 810 M612 735 L808 752 Q822 754 822 768 L822 792 Q822 804 808 804 L612 810';
  const prop = '<path d="M500 52 L700 52" stroke-width="6"/><circle cx="600" cy="62" r="12"/>';
  const ailerons = '<path d="M120 352 L330 340 M1080 352 L870 340" stroke-dasharray="10 8" stroke-width="3"/>';
  const canopy = '<path d="M565 175 Q600 150 635 175 L640 250 L560 250 Z" stroke-width="3"/>';
  const struts = layout === 'high' ? '<path d="M560 420 L380 330 M640 420 L820 330" stroke-dasharray="6 6" stroke-width="3"/>' : '';
  const body =
    layout === 'high'
      ? `<path d="${fuselage}"/>${canopy}${struts}<path d="${wingL} Z" fill="#E7EBF0"/><path d="${wingR} Z" fill="#E7EBF0"/><path d="M538 255 L662 255 L662 372 L538 372 Z" fill="#E7EBF0"/>`
      : `<path d="${wingL}"/><path d="${wingR}"/><path d="${fuselage}"/>${canopy}`;
  return { width: w, height: h, svg: wrap(w, h, `${body}${ailerons}<path d="${stab}"/>${prop}`) };
}

function front(layout: WingLayout): Silhouette {
  const w = 1200;
  const h = 560;
  const wingY = layout === 'high' ? 175 : 330;
  const dihedral = layout === 'high' ? 0 : 28;
  const wings =
    `<path d="M530 ${wingY} L50 ${wingY - dihedral} L50 ${wingY - dihedral + 18} L530 ${wingY + 22} Z"/>` +
    `<path d="M670 ${wingY} L1150 ${wingY - dihedral} L1150 ${wingY - dihedral + 18} L670 ${wingY + 22} Z"/>`;
  const fuselage = '<ellipse cx="600" cy="270" rx="78" ry="100"/><ellipse cx="600" cy="255" rx="48" ry="38" stroke-width="3"/>';
  const fin = '<path d="M592 170 L596 40 L604 40 L608 170"/>';
  const stab = '<path d="M440 196 L760 196" stroke-width="6"/>';
  const prop = '<circle cx="600" cy="290" r="150" stroke-dasharray="14 12" stroke-width="2"/><circle cx="600" cy="290" r="14"/>';
  const gear =
    '<path d="M600 370 L600 450"/><rect x="588" y="450" width="24" height="56" rx="10"/>' +
    '<path d="M560 340 L400 450 M640 340 L800 450"/><rect x="384" y="448" width="30" height="64" rx="12"/><rect x="786" y="448" width="30" height="64" rx="12"/>';
  const struts = layout === 'high' ? '<path d="M540 330 L360 195 M660 330 L840 195" stroke-width="3"/>' : '';
  return { width: w, height: h, svg: wrap(w, h, `${fin}${stab}${wings}${struts}${fuselage}${prop}${gear}`) };
}

function side(layout: WingLayout): Silhouette {
  const w = 1200;
  const h = 520;
  const fuselage =
    'M70 270 C70 220 120 195 200 190 L330 180 C380 140 470 120 560 140 L640 185 L1010 225 L1090 230 L1100 270 L1010 285 L640 315 C500 335 300 340 200 330 C120 325 70 310 70 270 Z';
  const fin = '<path d="M960 228 L1020 80 L1095 80 L1100 230"/><path d="M1050 82 L1060 228" stroke-dasharray="8 6" stroke-width="3"/>';
  const stab = '<path d="M930 260 L1090 252 L1090 268 L930 274 Z"/>';
  const window = '<path d="M350 182 C400 150 470 140 540 152 L600 190 L360 196 Z" stroke-width="3"/>';
  const wing =
    layout === 'high'
      ? '<path d="M330 150 L720 150 L720 166 L330 170 Z"/><path d="M420 300 L560 170" stroke-width="3"/>'
      : '<path d="M300 300 L700 300 L700 318 L300 322 Z"/>';
  const gear =
    '<path d="M180 330 L170 420"/><circle cx="170" cy="440" r="26"/>' +
    '<path d="M470 330 L500 420"/><circle cx="500" cy="440" r="32"/>';
  const prop = '<path d="M52 120 L52 420" stroke-width="7"/><path d="M52 270 L70 270"/>';
  return { width: w, height: h, svg: wrap(w, h, `<path d="${fuselage}"/>${window}${wing}${fin}${stab}${gear}${prop}`) };
}

/**
 * The cabin seen from above, nose up (the cabin view `innen`): instrument
 * panel, front seats, rear seats or bench, baggage area. Its size is
 * CABIN_PLACEHOLDER_SIZE from @fsv/core — interior pins are normalised to it
 * until the club uploads a real interior drawing.
 */
export function cabin(seats: CabinSeats): Silhouette {
  const w = CABIN_PLACEHOLDER_SIZE.width;
  const h = CABIN_PLACEHOLDER_SIZE.height;
  const shell = 'M500 60 C640 60 760 150 790 300 L820 640 L820 1180 C820 1320 700 1440 500 1440 C300 1440 180 1320 180 1180 L180 640 L210 300 C240 150 360 60 500 60 Z';
  const panel = '<path d="M260 330 Q500 250 740 330 L740 420 Q500 360 260 420 Z" fill="#E7EBF0"/>';
  const gauges = [330, 410, 590, 670].map((x) => `<circle cx="${x}" cy="365" r="22" stroke-width="3"/>`).join('');
  const sticks = '<path d="M375 470 L375 520 M625 470 L625 520" stroke-width="6"/>';
  const seat = (x: number, y: number) =>
    `<rect x="${x}" y="${y}" width="180" height="200" rx="28"/><rect x="${x + 10}" y="${y + 150}" width="160" height="60" rx="20" stroke-width="3"/>`;
  const front = seat(285, 560) + seat(535, 560);
  const rear = seats === 4 ? seat(285, 850) + seat(535, 850) : '';
  const baggageY = seats === 4 ? 1130 : 860;
  const baggage = `<rect x="260" y="${baggageY}" width="480" height="${1360 - baggageY}" rx="30" stroke-dasharray="14 10"/>`;
  const labels =
    `<text x="500" y="230" text-anchor="middle" font-family="sans-serif" font-size="34" fill="${INK}" opacity="0.6">Instrumentenbrett</text>` +
    `<text x="500" y="${baggageY + 70}" text-anchor="middle" font-family="sans-serif" font-size="34" fill="${INK}" opacity="0.6">Gepäckraum</text>`;
  return { width: w, height: h, svg: wrap(w, h, `<path d="${shell}"/>${panel}${gauges}${sticks}${front}${rear}${baggage}${labels}`) };
}

export type CabinSeats = 2 | 4;

/** Seats the cabin placeholder draws; two-seaters by type code, everything else four. */
export function seatsForType(code: string): CabinSeats {
  return ['DA20', 'C152', 'C150', 'AQUILA', 'A210', 'C42'].includes(code.toUpperCase()) ? 2 : 4;
}

export function silhouette(layout: WingLayout, view: BaseView): Silhouette {
  if (view === 'oben') return top(layout);
  if (view === 'vorne') return front(layout);
  return side(layout);
}

export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Which outline stands in for which type. The C172 is the only high-wing in the fleet. */
export function layoutForType(code: string): WingLayout {
  return code.toUpperCase() === 'C172' ? 'high' : 'low';
}
